import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

const GPUManagementPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [gpus, setGpus] = useState([]);
  const [vms, setVms] = useState([]);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedGpu, setSelectedGpu] = useState(null);
  const [selectedVm, setSelectedVm] = useState('');
  const [openTagDialog, setOpenTagDialog] = useState(false);
  const [editingGpu, setEditingGpu] = useState(null);
  const [newTag, setNewTag] = useState('');

  const fetchGPUs = async (refresh = false) => {
    setLoading(true);
    try {
      const response = await apiService.getGPUsWithTags(refresh);
      setGpus(response.data.gpus);
    } catch (error) {
      enqueueSnackbar('GPU 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchVMs = async () => {
    try {
      const response = await apiService.getVMs(true);
      setVms(response.data.vms);
    } catch (error) {
      enqueueSnackbar('VM 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchGPUs();
    fetchVMs();
  }, []);

  const handleRefresh = () => {
    fetchGPUs(true);
  };

  const handleOpenAssignDialog = (gpu) => {
    setSelectedGpu(gpu);
    setSelectedVm('');
    setOpenAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
    setSelectedGpu(null);
  };

  const handleAssignGpu = async () => {
    if (!selectedGpu || !selectedVm) {
      enqueueSnackbar('GPU와 VM을 선택해야 합니다.', { variant: 'warning' });
      return;
    }

    try {
      await apiService.assignGPU(selectedGpu.pci_id, selectedVm);
      enqueueSnackbar(`GPU가 VM '${selectedVm}'에 할당되었습니다.`, { variant: 'success' });
      fetchGPUs();
      handleCloseAssignDialog();
    } catch (error) {
      enqueueSnackbar(`GPU 할당 중 오류가 발생했습니다: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    }
  };

  const handleUnassignGpu = async (gpu) => {
    try {
      await apiService.unassignGPU(gpu.pci_id);
      enqueueSnackbar('GPU 할당이 해제되었습니다.', { variant: 'success' });
      fetchGPUs();
    } catch (error) {
      enqueueSnackbar(`GPU 할당 해제 중 오류가 발생했습니다: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    }
  };

  // 태그 편집 관련 함수들
  const handleOpenTagDialog = (gpu) => {
    setEditingGpu(gpu);
    setNewTag(gpu.tag || '');
    setOpenTagDialog(true);
  };

  const handleCloseTagDialog = () => {
    setOpenTagDialog(false);
    setEditingGpu(null);
    setNewTag('');
  };

  const handleUpdateTag = async () => {
    if (!editingGpu) return;

    try {
      await apiService.updateGPUTag(editingGpu.pci_id, newTag);
      enqueueSnackbar('GPU 태그가 업데이트되었습니다.', { variant: 'success' });
      fetchGPUs();
      handleCloseTagDialog();
    } catch (error) {
      enqueueSnackbar(`GPU 태그 업데이트 중 오류가 발생했습니다: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    }
  };

  const handleToggleBulkAssignable = async (gpu) => {
    const newValue = !gpu.bulk_assignable;
    try {
      await apiService.updateGPUBulkAssignable(gpu.pci_id, newValue);
      enqueueSnackbar(
        newValue ? `GPU ${gpu.pci_id}가 일괄할당 풀에 추가되었습니다.` : `GPU ${gpu.pci_id}가 일괄할당 풀에서 제거되었습니다.`,
        { variant: 'success' }
      );
      fetchGPUs();
    } catch (error) {
      enqueueSnackbar(`일괄할당 설정 오류: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">GPU 관리</Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          새로고침
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ overflow: 'auto', maxWidth: '100%' }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell>PCI ID</TableCell>
              <TableCell>모델</TableCell>
              <TableCell>태그</TableCell>
              <TableCell>장치 ID</TableCell>
              <TableCell>커널 드라이버</TableCell>
              <TableCell>IOMMU 그룹</TableCell>
              <TableCell>Passthrough</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>할당된 VM</TableCell>
              <TableCell align="center">일괄할당</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gpus.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    감지된 GPU가 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              gpus.map((gpu) => (
                <TableRow key={gpu.pci_id} hover>
                  <TableCell>{gpu.pci_id}</TableCell>
                  <TableCell>{gpu.model}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {gpu.tag ? (
                        <Chip 
                          size="small" 
                          label={gpu.tag} 
                          color="primary" 
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          태그 없음
                        </Typography>
                      )}
                      <Tooltip title="태그 편집">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenTagDialog(gpu)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{gpu.device_id}</TableCell>
                  <TableCell>{gpu.kernel_driver || '-'}</TableCell>
                  <TableCell>{gpu.iommu_group || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      color={gpu.is_vfio ? 'success' : 'warning'} 
                      label={gpu.is_vfio ? '가능' : '불가능'} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      color={gpu.assigned ? 'error' : 'success'} 
                      label={gpu.assigned ? '할당됨' : '사용 가능'} 
                    />
                  </TableCell>
                  <TableCell>{gpu.vm_name || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={gpu.bulk_assignable ? '일괄할당 풀에서 제거' : '일괄할당 풀에 추가'}>
                      <Switch
                        checked={!!gpu.bulk_assignable}
                        onChange={() => handleToggleBulkAssignable(gpu)}
                        color="secondary"
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {!gpu.assigned ? (
                      <Tooltip title="VM에 할당">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleOpenAssignDialog(gpu)}
                        >
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="할당 해제">
                        <IconButton 
                          color="error" 
                          onClick={() => handleUnassignGpu(gpu)}
                        >
                          <LinkOffIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* GPU 할당 다이얼로그 */}
      <Dialog open={openAssignDialog} onClose={handleCloseAssignDialog}>
        <DialogTitle>GPU 할당</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            <Typography variant="subtitle1" gutterBottom>
              {selectedGpu?.model} (PCI ID: {selectedGpu?.pci_id})
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>가상머신 선택</InputLabel>
              <Select
                value={selectedVm}
                label="가상머신 선택"
                onChange={(e) => setSelectedVm(e.target.value)}
              >
                {vms.map((vm) => (
                  <MenuItem key={vm.name} value={vm.name}>
                    {vm.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog} color="inherit">취소</Button>
          <Button onClick={handleAssignGpu} color="primary" variant="contained">
            할당
          </Button>
        </DialogActions>
      </Dialog>

      {/* GPU 태그 편집 다이얼로그 */}
      <Dialog open={openTagDialog} onClose={handleCloseTagDialog} maxWidth="sm" fullWidth>
        <DialogTitle>GPU 태그 편집</DialogTitle>
        <DialogContent>
          {editingGpu && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                GPU: {editingGpu.pci_id} - {editingGpu.model}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="태그"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                helperText="GPU를 구분하기 위한 사용자 정의 태그를 입력하세요"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTagDialog} color="inherit">취소</Button>
          <Button onClick={handleUpdateTag} color="primary" variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GPUManagementPage; 